import { Card, CardContent } from '@/components/ui/card';
import { FileText, DollarSign, Clock, AlertTriangle } from 'lucide-react';
import { InvoiceStats } from '@/types/invoice';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

interface InvoiceSummaryCardsProps {
  stats: InvoiceStats;
  isLoading?: boolean;
}

export function InvoiceSummaryCards({ stats, isLoading }: InvoiceSummaryCardsProps) {
  const { locale } = useCompanyLocale();
  // Whole-currency figures for KPI tiles ("$12,400"), in the tenant's currency
  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat(locale.locale, {
      style: 'currency',
      currency: locale.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  const cards = [
    {
      title: 'Total Invoiced',
      value: formatCurrency(stats.totalInvoiced),
      subtitle: `${stats.invoiceCount} invoices`,
      icon: FileText,
      iconColor: 'text-primary',
      bgColor: 'bg-primary/10',
      valueColor: '',
    },
    {
      title: 'Paid',
      value: formatCurrency(stats.totalPaid),
      subtitle: `${stats.paidCount} invoices`,
      icon: DollarSign,
      iconColor: 'text-success',
      bgColor: 'bg-success/10',
      valueColor: '',
    },
    {
      title: 'Outstanding',
      value: formatCurrency(stats.totalOutstanding),
      subtitle: `${stats.sentCount} awaiting payment`,
      icon: Clock,
      iconColor: 'text-info',
      bgColor: 'bg-info/10',
      valueColor: '',
    },
    {
      title: 'Overdue',
      value: formatCurrency(stats.totalOverdue),
      subtitle: `${stats.overdueCount} overdue`,
      icon: AlertTriangle,
      iconColor: 'text-destructive',
      bgColor: 'bg-destructive/10',
      valueColor: 'text-destructive',
    },
  ];

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardContent className="p-5">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-muted rounded w-24"></div>
                <div className="h-8 bg-muted rounded w-32"></div>
                <div className="h-3 bg-muted rounded w-20"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {cards.map((card) => (
        <Card key={card.title}>
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-medium text-muted-foreground">{card.title}</p>
                <p className={`mt-1.5 text-[26px] font-semibold leading-none tracking-tight tabular-nums ${card.valueColor}`}>
                  {card.value}
                </p>
                <p className="mt-1.5 text-xs text-muted-foreground tabular-nums">{card.subtitle}</p>
              </div>
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${card.bgColor}`}>
                <card.icon className={`h-4 w-4 ${card.iconColor}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
